import React, { useEffect, useState, useCallback } from 'react';
import {
  required,
  hasLength,
  onlyIntegers,
  numberLessThanOrEqualTo,
  numberGreaterThanOrEqualTo,
} from 'redux-freeform';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  ButtonWithAction,
  Cluster,
  FormContainer,
  FormInput,
  Text,
  util,
  RadioGroup,
  Alert,
} from '@thecb/components';

import {
  debounce,
  formatCurrencyInput,
  formatPrice,
} from '../../../utils/helpers';

import {
  cardReducer,
  cardMapStateToProps,
  cardMapDispatchToProps,
} from '../CheckoutForm.state';
import { useDispatch, useSelector } from 'react-redux';
import {
  getServiceFee,
  getConvenienceFee,
  setAwaitCreditCardTimeRemaining,
  setCardPaymentType,
  isAwaitingCreditCard,
  maxSecondsToInsertCard,
  maxSecondsToInsertCardDps,
} from '../Checkout.state';
import { fetchAgencies } from '../../products/Products.state';
import { CARD_COUNTDOWN_MODAL, MAGTEK_SWIPE_CARD_MODAL } from '../Checkout';
import {
  CARD,
  CARD_PAYMENT_TYPES,
  CC_READER_TYPE,
} from '../../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { HalfWidthFieldWrapper } from '../../../components/styled';
import { requireErrors, paymentAmountErrors } from './shared-constants';

const cardSubmissionTypeRadioGroupConfig = [
  { id: 'card-reader', value: 'Card reader', labelText: 'Card Reader' },
  { id: 'manual-entry', value: 'Manual entry', labelText: 'Manual Entry' },
];

const cardNumberErrors = {
  [onlyIntegers.error]: 'Must only contain numbers',
  [required.error]: 'Card Number is required',
  [hasLength.error]: 'Must be between 13-16 digits',
};

const expMonthErrors = {
  [onlyIntegers.error]: 'Must only contain numbers',
  [required.error]: 'Expiration Month is required',
  [hasLength.error]: 'Must be two digits',
};

const expYearErrors = {
  [onlyIntegers.error]: 'Must only contain numbers',
  [required.error]: 'Expiration Year is required',
  [hasLength.error]: 'Must be four digits',
};

const cvvErrors = {
  [onlyIntegers.error]: 'Must only contain numbers',
  [required.error]: 'CVV is required',
  [hasLength.error]: 'Must be 3 or 4 digits',
};

const zipcodeErrors = {
  [onlyIntegers.error]: 'Must only contain numbers',
  [required.error]: 'Zip code is required',
  [numberGreaterThanOrEqualTo.error]: 'Must be at least 5 digits',
};

let CardCheckoutForm = ({
  actions,
  fields,
  openProcessingModal,
  makePayment,
  creditCardReaderType,
  displayCreditCardReaderAlert,
  remainingBalance,
  processBtnText,
  splitTenderAllowed,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const flags = useFlags();
  const [isTyping, setIsTyping] = useState(false);
  const [errors, handleErrors] = useState(false);
  const isDatacap =
    creditCardReaderType === CC_READER_TYPE.TRANCLOUD ||
    creditCardReaderType === CC_READER_TYPE.DC_DIRECT;
  const isManualEntrySelected =
    fields.submission_type.rawValue === 'Manual entry';

  const enable_manual_card_entry = useSelector(
    (state) =>
      state.terminalLogin?.locations?.selectedLocation
        ?.enable_manual_card_entry,
  );
  const agency_id = useSelector((state) =>
    state.cart?.order?.agencies ? state.cart?.order?.agencies[0]?.id : null,
  );
  const agencies = useSelector((state) => state.products?.agencies?.data);
  const agency = agencies.find((agency) => agency.id === agency_id);
  const service_fee_percentage =
    agency?.agency_fee_config?.service_fee_percentage;
  const minimum_service_fee = agency?.agency_fee_config?.minimum_service_fee;
  const {
    isLoading: isLoadingCheckout,
    serviceFeeForCurrentAmount,
    visitId,
    isPollingForCCResponse,
    convenienceFee,
    maxCardAmountAllowed,
  } = useSelector((state) => state.checkout);
  const { fetching: isLoadingCart } = useSelector((state) => state.cart);
  const { payerNameRequired } = useSelector(
    (state) => state.shared.clientConfig,
  );

  const isLoading = isLoadingCheckout || isLoadingCart;

  useEffect(() => {
    dispatch(fetchAgencies());
  }, []);

  useEffect(() => {
    if (payerNameRequired || isManualEntrySelected) {
      actions.fields.first_name.addValidator(required());
      actions.fields.last_name.addValidator(required());
    }
    if (!payerNameRequired && !isManualEntrySelected) {
      actions.fields.first_name.removeValidator(required());
      actions.fields.last_name.removeValidator(required());
    }
  }, [payerNameRequired, isManualEntrySelected]);

  useEffect(() => {
    if (remainingBalance) {
      if (service_fee_percentage) {
        dispatch(getServiceFee({ amount: remainingBalance }));
      }
      actions.fields.amount.set(formatCurrencyInput(remainingBalance));
    }
  }, [remainingBalance, service_fee_percentage]);

  useEffect(() => {
    if (agency_id) {
      dispatch(getConvenienceFee({ agency_id: agency_id }));
    }
  }, [agency_id]);

  useEffect(() => {
    actions.fields.amount.removeValidator(numberLessThanOrEqualTo());

    if (maxCardAmountAllowed > 0) {
      actions.fields.amount.addValidator(
        numberLessThanOrEqualTo(maxCardAmountAllowed),
      );
    }
  }, [maxCardAmountAllowed]);

  const requestServiceFee = debounce((amount) => {
    if (Number(amount) >= 0) {
      dispatch(getServiceFee({ amount }));
    }
    setIsTyping(false);
  }, 500);

  const debounceServiceFeeRequest = useCallback((amount) => {
    requestServiceFee(amount);
  }, []);

  const handleCardSubmit = (cardPaymentType) =>
    util.general.generateClickHandler(
      { fields },
      () => handleErrors(true),
      () => {
        switch (creditCardReaderType) {
          case CC_READER_TYPE.DC_DIRECT:
          case CC_READER_TYPE.TRANCLOUD: {
            if (flags['pay-with-device-payment-service']) {
              dispatch(
                setAwaitCreditCardTimeRemaining(maxSecondsToInsertCardDps),
              );
              dispatch(setCardPaymentType(cardPaymentType));
            } else {
              dispatch(setAwaitCreditCardTimeRemaining(maxSecondsToInsertCard));
              dispatch(isAwaitingCreditCard(cardPaymentType));
            }
            openProcessingModal(CARD_COUNTDOWN_MODAL);
            makePayment({
              amount_given: Number(fields.amount.rawValue),
              ...(cardPaymentType === CARD_PAYMENT_TYPES.MANUAL && {
                acct_no: 'Prompt',
              }),
              ...(payerNameRequired && {
                first_name: fields.first_name.rawValue,
                last_name: fields.last_name.rawValue,
              }),
              payment_tender_type: CARD,
              service_fee: serviceFeeForCurrentAmount,
              convenience_fee: convenienceFee,
              navigate,
              location,
              cc_reader_type: creditCardReaderType,
              visit_id: visitId,
            });
            break;
          }
          case CC_READER_TYPE.MAGTEK: {
            if (
              enable_manual_card_entry &&
              cardPaymentType === CARD_PAYMENT_TYPES.MANUAL
            ) {
              makePayment({
                amount_given: Number(fields.amount.rawValue),
                service_fee: serviceFeeForCurrentAmount,
                convenience_fee: convenienceFee,
                first_name: fields.first_name.rawValue,
                last_name: fields.last_name.rawValue,
                number: fields.number.rawValue,
                month: fields.month.rawValue,
                year: fields.year.rawValue,
                cvv: fields.cvv.rawValue,
                zipcode: fields.zipcode.rawValue,
                payment_tender_type: CARD,
                manual_entry: true,
                navigate,
                location,
                cc_reader_type: CC_READER_TYPE.MAGTEK,
              });
            } else {
              openProcessingModal(MAGTEK_SWIPE_CARD_MODAL);
            }
            break;
          }
          default: {
            displayCreditCardReaderAlert();
            break;
          }
        }
      },
    );

  paymentAmountErrors[numberLessThanOrEqualTo.error] =
    `The maximum allowed amount for card payment is ${formatPrice(Number(maxCardAmountAllowed))}`;

  const amountOverMaxCardAmount =
    maxCardAmountAllowed > 0 &&
    parseFloat(fields.amount.rawValue) > maxCardAmountAllowed;

  return (
    <FormContainer>
      <FormInput
        disabled={!splitTenderAllowed}
        onKeyDown={(e) => {
          if (service_fee_percentage) {
            const isNumber = e.key !== ' ' && isFinite(e.key);
            const ALLOWABLE_KEYS = ['Enter', 'Backspace', '.'];
            if (!isTyping && (ALLOWABLE_KEYS.includes(e.key) || isNumber)) {
              setIsTyping(true);
            }
          }
        }}
        onKeyUp={() => {
          if (service_fee_percentage) {
            debounceServiceFeeRequest(fields.amount.rawValue || 0);
          }
        }}
        id="card-payment-amt"
        type="number"
        labelTextWhenNoError="Payment Amount"
        errorMessages={paymentAmountErrors}
        field={fields.amount}
        errors={fields.errors}
        fieldActions={actions.fields.amount}
        labelTextVariant="p"
        errorTextVariant="p"
      />
      {enable_manual_card_entry && (
        <RadioGroup
          field={fields.submission_type}
          fieldActions={actions.fields.submission_type}
          groupName="submission-type"
          headingText="Submission Type"
          headingFontSize="1rem"
          config={cardSubmissionTypeRadioGroupConfig}
          extraStyles={`margin-bottom: 8px;`}
        />
      )}
      {(payerNameRequired || isManualEntrySelected) && (
        <HalfWidthFieldWrapper>
          <FormInput
            id="card-first-name"
            labelTextWhenNoError="First Name"
            errorMessages={requireErrors('First Name is required')}
            field={fields.first_name}
            showErrors={errors}
            fieldActions={actions.fields.first_name}
            labelTextVariant="p"
            errorTextVariant="p"
          />
          <FormInput
            id="card-last-name"
            labelTextWhenNoError="Last Name"
            errorMessages={requireErrors('Last Name is required')}
            field={fields.last_name}
            showErrors={errors}
            fieldActions={actions.fields.last_name}
            labelTextVariant="p"
            errorTextVariant="p"
          />
        </HalfWidthFieldWrapper>
      )}
      {isManualEntrySelected && (
        <>
          <FormInput
            id="card-number"
            labelTextWhenNoError="Card Number"
            errorMessages={cardNumberErrors}
            field={fields.number}
            showErrors={errors}
            fieldActions={actions.fields.number}
            labelTextVariant="p"
            errorTextVariant="p"
          />
          <HalfWidthFieldWrapper>
            <FormInput
              id="exp_month"
              labelTextWhenNoError="Expiration Month (MM)"
              errorMessages={expMonthErrors}
              field={fields.month}
              showErrors={errors}
              fieldActions={actions.fields.month}
              labelTextVariant="p"
              errorTextVariant="p"
            />
            <FormInput
              id="exp_year"
              labelTextWhenNoError="Expiration Year (YYYY)"
              errorMessages={expYearErrors}
              field={fields.year}
              showErrors={errors}
              fieldActions={actions.fields.year}
              labelTextVariant="p"
              errorTextVariant="p"
            />
          </HalfWidthFieldWrapper>
          <HalfWidthFieldWrapper>
            <FormInput
              id="cvv"
              labelTextWhenNoError="CVV"
              errorMessages={cvvErrors}
              field={fields.cvv}
              showErrors={errors}
              fieldActions={actions.fields.cvv}
              labelTextVariant="p"
              errorTextVariant="p"
            />
            <FormInput
              id="zipcode"
              labelTextWhenNoError="Zip code"
              errorMessages={zipcodeErrors}
              field={fields.zipcode}
              showErrors={errors}
              fieldActions={actions.fields.zipcode}
              labelTextVariant="p"
              errorTextVariant="p"
            />
          </HalfWidthFieldWrapper>
        </>
      )}
      {service_fee_percentage && (
        <Alert
          heading="Service Fee"
          showQuitLink={false}
          extraStyles={`margin-bottom: 0.875rem;`}
        >
          <Text id="service-fee-alert-text">
            {`There is a service fee of ${parseFloat(service_fee_percentage).toFixed(2)}% ${
              minimum_service_fee
                ? `or ${formatPrice(minimum_service_fee)} minimum`
                : ''
            } for all card payments. The fee for this transaction is ${
              !serviceFeeForCurrentAmount || isLoading || isTyping
                ? '...'
                : `${formatPrice(serviceFeeForCurrentAmount)}.`
            }`}
          </Text>
        </Alert>
      )}
      {!!convenienceFee && (
        <Alert
          heading="Transaction Fee"
          showQuitLink={false}
          extraStyles={`margin-bottom: 0.875rem;`}
        >
          <Text id="convenience-fee-alert-text">
            {`The fee for this transaction is $${parseFloat(convenienceFee).toFixed(2)}.`}
          </Text>
        </Alert>
      )}
      <Cluster>
        <ButtonWithAction
          text={'Back to Cart'}
          variant="secondary"
          dataQa="view-cart"
          action={() => navigate('/pos/cart')}
          extraStyles="margin-left: 0"
          textExtraStyles="font-size: 1rem;"
        />
        {isDatacap && (
          <ButtonWithAction
            text={'Manual Card Entry'}
            action={(e) => handleCardSubmit(CARD_PAYMENT_TYPES.MANUAL)(e)}
            textExtraStyles="font-size: 1rem;"
          />
        )}
        <ButtonWithAction
          id="process-card-btn"
          textExtraStyles="font-size: 1rem;"
          disabled={
            isLoading ||
            isTyping ||
            fields.amount.rawValue <= 0 ||
            fields.amount.hasErrors ||
            isPollingForCCResponse ||
            amountOverMaxCardAmount
          }
          isLoading={isLoading || isTyping}
          text={processBtnText}
          action={(e) =>
            handleCardSubmit(
              isManualEntrySelected
                ? CARD_PAYMENT_TYPES.MANUAL
                : CARD_PAYMENT_TYPES.AUTOMATIC,
            )(e)
          }
        />
      </Cluster>
    </FormContainer>
  );
};

CardCheckoutForm.reducer = cardReducer;
CardCheckoutForm.mapStateToProps = cardMapStateToProps;
CardCheckoutForm.mapDispatchToProps = cardMapDispatchToProps;

export default CardCheckoutForm;
